// ============================================================
// SUPABASE CLIENT + AUTH LAYER
// ============================================================
// Credenziali lette da env Vite. In dev si usa .env, in produzione
// (build) vengono embedded. Mai committare .env reali.
// ============================================================
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { createClient, SupabaseClient, type Session, type User } from '@supabase/supabase-js';
import type { AppUser } from '@/types';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string || '').trim();
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY as string || '').trim();

let _client: SupabaseClient | null = null;

/** Restituisce true se Supabase è configurato. */
export function isSupabaseConfigured(): boolean {
  return !!SUPABASE_URL && !!SUPABASE_ANON_KEY;
}

/** Crea (cached) o restituisce il client Supabase. */
export function getSupabase(): SupabaseClient {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase non configurato. Crea un file .env con VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY (vedi .env.example).');
  }
  if (_client) return _client;
  _client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    },
  });
  return _client;
}

// ============================================================
// AUTH API
// ============================================================

export interface AuthResult {
  ok: boolean;
  message: string;
  user?: AppUser;
}

function toAppUser(u: User): AppUser {
  const provider = u.app_metadata?.provider === 'google' ? 'google'
    : u.app_metadata?.provider === 'apple' ? 'apple'
    : u.app_metadata?.provider === 'email' ? 'email'
    : 'unknown';
  return {
    id: u.id,
    email: u.email ?? '',
    displayName: u.user_metadata?.full_name ?? u.user_metadata?.name ?? u.email ?? '',
    avatarUrl: u.user_metadata?.avatar_url ?? u.user_metadata?.picture ?? null,
    provider,
    createdAt: u.created_at ?? new Date().toISOString(),
  };
}

/** Registrazione con email + password (richiede conferma via email). */
export async function signUpWithEmail(email: string, password: string): Promise<AuthResult> {
  const client = getSupabase();
  const { data, error } = await client.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: window.location.origin + '/auth/callback',
    },
  });
  if (error) return { ok: false, message: error.message };
  if (data.user && !data.session) {
    return {
      ok: true,
      message: 'Registrazione completata. Controlla la tua email per confermare l\'account.',
    };
  }
  if (data.user && data.session) {
    return { ok: true, message: 'Registrazione completata.', user: toAppUser(data.user) };
  }
  return { ok: false, message: 'Registrazione non riuscita.' };
}

/** Login con email + password. */
export async function signInWithEmail(email: string, password: string): Promise<AuthResult> {
  const client = getSupabase();
  const { data, error } = await client.auth.signInWithPassword({ email, password });
  if (error) return { ok: false, message: error.message };
  if (!data.user) return { ok: false, message: 'Login non riuscito.' };
  return { ok: true, message: 'Login riuscito', user: toAppUser(data.user) };
}

/**
 * Login con Google OAuth.
 *
 * - **Web**: redirect OAuth standard gestito da Supabase (nessun client ID nel
 *   frontend; la configurazione OAuth risiede nel dashboard Supabase).
 * - **Mobile (Android/iOS)**: usa il plugin nativo `@capgo/capacitor-social-login`
 *   che DEVE essere inizializzato in `authStore.init()` con i client ID presi
 *   da `.env`. Qui chiamiamo solo `SocialLogin.login()` con gli scope richiesti;
 *   i client ID NON vanno passati di nuovo a `login()` (sono già noti al plugin
 *   tramite `initialize()`: passarli di nuovo con valori `undefined` rompeva il
 *   provider di Google — era la causa dell'errore "getCredentialAsync no
 *   provider dependencies found").
 */
export async function signInWithGoogle(): Promise<void> {
  const client = getSupabase();

  // ---------- WEB (redirect OAuth standard) ----------
  if (!Capacitor.isNativePlatform()) {
    await client.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/auth/callback',
      },
    });
    return; // Il browser viene reindirizzato, quindi l'esecuzione si ferma qui
  }

  // ---------- MOBILE (Android/iOS) ----------
  // Sanity check: i client ID devono essere stati definiti in .env
  const webClientId = (import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined)?.trim();
  if (!webClientId) {
    throw new Error(
      'VITE_GOOGLE_WEB_CLIENT_ID non configurato. ' +
      'Aggiungilo al file .env (vedi .env.example).'
    );
  }

  try {
    // 1. Login nativo Google.
    //    I client ID sono già noti al plugin grazie a SocialLogin.initialize()
    //    chiamato in authStore.init().
    //
    //    ⚠️ NON passiamo `scopes` qui: il plugin @capgo/capacitor-social-login
    //    v8 richiede la modifica di MainActivity.java per poter usare scope
    //    espliciti (errore: "You CANNOT use scopes without modifying the
    //    main activity"). I scope di DEFAULT del plugin sono già `profile`
    //    ed `email`, che sono esattamente quelli necessari per ottenere un
    //    idToken valido che Supabase possa verificare. Se in futuro servono
    //    scope aggiuntivi (es. `openid`, `https://www.googleapis.com/auth/...`),
    //    modifica MainActivity.java come descritto in README.md → sezione
    //    "Google OAuth con scope personalizzati".
    //
    //    🛡️ STRATEGIA DI FALLBACK: se la prima chiamata fallisce con
    //    "no provider dependencies found", è probabile che l'androidClientId
    //    passato in initialize() abbia un SHA-1 in Google Cloud Console che
    //    non matcha quello del keystore con cui è firmato l'APK. Riproviamo
    //    reinizializzando il plugin con SOLO webClientId (che è il parametro
    //    realmente usato da Credential Manager).
    let loginResult: any;
    try {
      loginResult = await SocialLogin.login({
        provider: 'google',
        options: {},
      });
    } catch (firstErr: any) {
      const msg = (firstErr?.message ?? '').toLowerCase();
      console.warn('[auth] Primo tentativo login Google fallito:', firstErr?.message ?? firstErr);

      // Fallback solo per l'errore specifico di Credential Manager
      if (msg.includes('no provider dependencies found') || msg.includes('getcredentialasync')) {
        console.log('[auth] Tentativo fallback: re-inizializzo SocialLogin con solo webClientId');
        const fallbackConfig: any = { webClientId, forceAuthCode: false };
        await SocialLogin.initialize({ google: fallbackConfig } as any);
        console.log('[auth] SocialLogin re-inizializzato (fallback). Riprovo il login...');
        loginResult = await SocialLogin.login({
          provider: 'google',
          options: {},
        });
      } else {
        // Errore diverso: rilancia senza fallback
        throw firstErr;
      }
    }

    // 2. Estrazione dell'idToken dalla risposta.
    //    La struttura può variare leggermente in base alla versione del plugin;
    //    proviamo i percorsi più comuni.
    let idToken: string | undefined;
    if (loginResult && typeof loginResult === 'object') {
      const resultObj = (loginResult as any).result;
      if (resultObj && typeof resultObj === 'object') {
        idToken = resultObj.idToken ?? resultObj.credential?.idToken;
      }
      // Fallback se il token si trova direttamente nell'oggetto principale
      if (!idToken && (loginResult as any).idToken) {
        idToken = (loginResult as any).idToken;
      }
    }

    if (!idToken) {
      console.warn('[auth] Struttura ricevuta da SocialLogin:', loginResult);
      throw new Error('Nessun idToken ricevuto da Google');
    }

    // 3. Scambia l'idToken con una sessione Supabase.
    //    L'audience dell'idToken DEVE corrispondere a VITE_GOOGLE_WEB_CLIENT_ID
    //    (lo stesso configurato in Supabase → Authentication → Providers → Google).
    const { data, error } = await client.auth.signInWithIdToken({
      provider: 'google',
      token: idToken,
    });

    if (error) throw new Error(error.message);
    if (!data.user) throw new Error('Login Google non riuscito: utente non trovato');

    // 4. Successo: l'aggiornamento della sessione avverrà automaticamente tramite onAuthChange
    console.log('[auth] Login Google completato con successo');
  } catch (err: any) {
    // Rilancia un errore pulito che verrà catturato dal chiamante (es. AuthPage.handleGoogle)
    throw new Error(err?.message ?? 'Errore durante il login Google');
  }
}

/** Resend email di conferma. */
export async function resendConfirmationEmail(email: string): Promise<AuthResult> {
  const client = getSupabase();
  const { error } = await client.auth.resend({
    type: 'signup',
    email,
    options: { emailRedirectTo: window.location.origin + '/auth/callback' },
  });
  if (error) return { ok: false, message: error.message };
  return { ok: true, message: 'Email di conferma reinviata.' };
}

/** Password reset email. */
export async function resetPassword(email: string): Promise<AuthResult> {
  const client = getSupabase();
  const { error } = await client.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin + '/auth/callback',
  });
  if (error) return { ok: false, message: error.message };
  return { ok: true, message: 'Email di reset inviata. Controlla la casella.' };
}

/** Logout. */
export async function signOut(): Promise<void> {
  const client = getSupabase();
  await client.auth.signOut();
}

/** Restituisce la sessione corrente. */
export async function getSession(): Promise<Session | null> {
  const client = getSupabase();
  const { data } = await client.auth.getSession();
  return data.session;
}

/** Restituisce l'utente corrente. */
export async function getCurrentUser(): Promise<AppUser | null> {
  const client = getSupabase();
  const { data } = await client.auth.getUser();
  if (!data.user) return null;
  return toAppUser(data.user);
}

/** Sottoscrive ai cambi di stato auth. */
export function onAuthChange(cb: (user: AppUser | null) => void): () => void {
  const client = getSupabase();
  const { data } = client.auth.onAuthStateChange((_event, session) => {
    cb(session?.user ? toAppUser(session.user) : null);
  });
  return () => data.subscription.unsubscribe();
}